// packages imports
import { Box, Grid, Typography, Select, MenuItem } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { InfoOutlined } from '@mui/icons-material'
import { useEffect } from 'react'
import { GRAPHQL_QUERY_POLICY } from '../../../../constants'

// components imports
import BillingCyclesList from './BillingCyclesList'
import CheckboxController from '../../../common/CheckboxController'
import { CustomController } from '../../../common/CustomController'

// others
import palette from '../../../../theme/palette'
import BankAccountsList from './BankAccountsList'
import { AccountType, useFetchRetainerAccountLazyQuery } from '../../../../../generated'
import { CustomDatePickerController } from '../../../common/CustomDatePickerRController'

const FLAT_FEE_HOURS_NOTIFICATION_THRESHOLD_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50]

const InfoTooltip = ({ info }: { info: string }) => (
  <Box display="flex" gap={1} pt={1} sx={{ minHeight: '55px' }}>
    <InfoOutlined sx={{ fontSize: '16px', color: '#000000' }} />
    <Typography variant="caption" color="#000000">
      {info}
    </Typography>
  </Box>
)

const HybridBillingForm = () => {
  const { control, setValue, watch } = useFormContext()

  const [fetchRetainerAccount] = useFetchRetainerAccountLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {},
    onCompleted(data) {
      if (data?.fetchRetainerAccount) {
        setValue('billing.retainerAccountId', data.fetchRetainerAccount.id)
      }
    }
  })

  useEffect(() => {
    const retainerAccountId = watch('billing.retainerAccountId')

    if (!retainerAccountId) {
      fetchRetainerAccount()
    }
  }, [fetchRetainerAccount, watch])

  // const [billEntireFlatFeeUpfront] = watch(['billing.billEntireFlatFeeUpfront'])

  return (
    <Grid container item mt={1.5}>
      <Grid item xs={12} md={6} pr={{ md: 3 }} sx={{ borderRight: { md: 1 }, borderColor: palette.lightGrayThree }}>
        <Typography variant="h3" textTransform="uppercase" fontSize={16} fontWeight={700}>
          Flat Fee
        </Typography>
        <InfoTooltip info="A fixed fee that will be charged for a certain number of hours" />

        <Box mt={1.5} display="flex" flexDirection="column" gap={2.5}>
          <CustomController
            fieldType="number"
            controllerName="billing.flatFee"
            controllerLabel="Flat Fee ($)"
            dollarAdornment
            sx={{ my: 0 }}
          />
          {/* <CheckboxController
            name="billing.billEntireFlatFeeUpfront"
            label="Bill Entire Amount Upfront"
            sx={{ my: -1 }}
          />

          {!billEntireFlatFeeUpfront && (
            <>
              <CustomController
                fieldType="number"
                controllerName="billing.upfrontFlatFee"
                controllerLabel="Upfront Amount ($)"
                dollarAdornment
                sx={{ my: 0 }}
              />
              <CustomController
                fieldType="number"
                controllerName="billing.monthlyFlatFee"
                controllerLabel="Monthly Payment ($)"
                dollarAdornment
                sx={{ my: 0 }}
              />
            </>
          )} */}

          <CheckboxController
            name="setting.notifyOnFlatFeeHoursThreshold"
            sx={{ mt: 1 }}
            label={
              <>
                Send email & text to attorney when
                <Controller
                  name="setting.flatFeeHoursNotificationThreshold"
                  control={control}
                  defaultValue={20}
                  render={({ field }) => (
                    <Select
                      {...field}
                      size="small"
                      sx={{ mx: 1, p: 0, color: palette.primary.main }}
                      variant="standard"
                    >
                      {FLAT_FEE_HOURS_NOTIFICATION_THRESHOLD_OPTIONS.map(option => (
                        <MenuItem key={option} value={option}>
                          {option}%
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                flat fee is remaining
              </>
            }
          />
          <CheckboxController
            name="setting.sendFlatFeeHoursExhaustedNotification"
            label="Send email & text to attorney when flat fee is exhausted"
          />
        </Box>
      </Grid>

      <Grid item xs={12} md={6} pl={{ md: 3 }} mt={{ xs: 3, md: 0 }}>
        <Typography variant="h3" textTransform={'uppercase'} fontSize={16} fontWeight={700}>
          Hourly
        </Typography>
        <InfoTooltip info="Billing will switch to hourly once the max allowed hours for flat fee are used. Hourly rate that is set in the legal team section will be used" />

        <Box mt={1.5} display="flex" flexDirection="column" gap={2.5}>
          <CustomController
            fieldType="number"
            controllerName="billing.retainerAmount"
            controllerLabel="Retainer Amount"
            dollarAdornment
            sx={{ my: 0 }}
          />
          <BankAccountsList name="billing.retainerAccountId" title="Bank Account"/>
          <InfoTooltip info="Make sure to choose the bank account correctly. For Hybrid Billing, bank account for retainer amount should be trust account but you can choose any bank account." />
          <BillingCyclesList name="billing.billingCycle" title="Billing Cycle" />
          <CustomController
            fieldType="number"
            controllerName="billing.minimumFundsThreshold"
            controllerLabel="Minimum Funds Threshold"
            dollarAdornment
            sx={{ my: 0 }}
          />
        </Box>
      </Grid>
      <Grid item md={6} xs={12}>
        <Box mt={2.5}>
          <CustomDatePickerController
            controllerName="billing.pauseBillingUntil"
            controllerLabel="Billing Paused Until"
            sx={{ my: 0 }}
          />
          <InfoTooltip info="Billing paused means that the monthly retainer amount and special payments (if any) will not be charged until the date (if selected) in above field. However, all the expense entries, cost entries or other legal fee entries will still be part of the monthly bill." />
        </Box>
      </Grid>
    </Grid>
  )
}

export default HybridBillingForm
